import { AfterViewInit, Component, computed, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroupValue } from '../../../share/form.utils';
import { Store } from '@ngrx/store';
import { JoinFormVm } from './join-form.vm';
import {
  getIsLoadingExecution,
  getIsLoadingRecostValidation,
  getJoinFormActiveStep,
  getJoinFormMedicalPrice,
  getJoinFormPromoCodeWarningRequireAutoRenew,
  getJoinFormOfferSummary,
  getJoinFormTotalCost,
  getJoinFormValues,
  getJoinMessageError,
  JoinActions,
  JoinForm,
  JoinFormStep,
  getJoinFormMemberInfoLabel,
  getJoinFormPage,
  getJoinFormSelectedLevel,
  getJoinFormExecutionData,
  JoinPayParams,
  getJoinFormShowGiftRecipient,
  getShowMedicalPlanGiftInfo,
  getShowMedicalPlanCurrentClub,
  getTermAndConditions,
  getJoinFormStates,
  getAutoRenewTooltipText$,
  getJoinFormMembershipLevelChanged,
  getJoinFormMembershipRvChanged,
  getShowBirthday,
  getShowPhoneNumber,
  getShowGiftMessage,
  getAllowZeroPrice,
  getIsMonthlyBilling,
} from '@aaa/emember/store-join';
import { AppStore } from '@aaa/emember/store-types';
import { getQueryAutoRenewQueryParam } from '../../../../store/router/router.selectors';
import { getAllStates } from '../../../../store/states/states.selectors';
import { getIsLoadingPaymentForm, getIsPaymentGenerating, getPayment } from '@aaa/emember/store-payment';
import { getMembershipOfferFilter } from '../../../../store/membership-offers/membership-offers.selectors';
import { combineLatest, delay, first, map, of, switchMap, withLatestFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AnalyticsActions } from '../../../../store/analytics';
import {
  getHideApplyPromo,
  getHideAddAssociate,
  getIsDisabledChangeAutoRenew,
  getIsNapamapProgramCode,
  getIsValidProgramCode,
  getPriceOfferCouponAlertType,
  getPriceOfferCouponDescription,
  getPriceOfferCouponMessage,
  getHasAutoRenewMembershipLevel,
  getPriceOfferPromoDescription,
} from '@aaa/emember/store-price-offers';
import { isFlagEnabled } from '../../../../store/feature-flags';
import { FeatureName } from '../../../../store/feature-flags/types';
import { environment } from '../../../../../environments/environment';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PaymentFormComponent } from '@aaa/emember/share/payment-form';
import { AppEnv, ClubApp } from '../../../../types';
import {
  getDefaultAdditionalNotes,
  getSouthJerseyAdditionalNotes,
} from '../../../../constants/add-associate-additional-notes';
import { ConnectFormDirective } from '../../../share/form/directives/connect-form.directive';
import { ReactiveFormsModule } from '@angular/forms';
import { AlertComponent } from '../../../share/alert';
import { AsyncPipe } from '@angular/common';
import { GiftRecipientInfoFormComponent } from '../../../share/gift-recipient-info-form';
import { MembershipAssociateFormComponent } from '../../../share/membership-associate-form';
import { ButtonComponent } from '../../../share/ava-button/ava-button.component';
import { ButtonLinkComponent } from '../../../share/ava-link/ava-link.component';
import { MemberInfoFormComponent } from '@aaa/emember/share/member-info-form';
import { BillingFormComponent } from '../../../share/billing-form';
import { FormCheckBoxComponent } from '../../../share/form/controls/checkbox';
import { FormInputPromoCodeComponent } from '../../../share/form/controls/input-promocode/input-promocode';
import { OfferSummaryCostComponent } from '../../../share/summaries/offer-summary-cost/offer-summary-cost';
import { TermsAndConditionComponent } from '../../../share/terms-and-condition/terms-and-condition';
import { AvaButtonPayComponent } from '../../../share/ava-button-pay/ava-button-pay.component';
import { JoinSideBarSummaryComponent } from '../join-sidebar-summary/join-sidebar-summary';
import {
  AccordionComponent,
  ExpansionPanelComponent,
  ExpansionPanelContentComponent,
  ExpansionPanelHeaderComponent,
} from '../../../share/collapse';
import { clubId, clubOption } from '../../../../signals';
import { cardTypes } from '../../../../constants/card-types';
import { CardFormat } from '@aaa/interface-joinRenew-membership-membershipConnectSuite';

@Component({
  selector: 'ava-join-form',
  standalone: true,
  templateUrl: './join-form.html',
  imports: [
    ConnectFormDirective,
    ReactiveFormsModule,
    AlertComponent,
    AsyncPipe,
    GiftRecipientInfoFormComponent,
    MembershipAssociateFormComponent,
    ButtonComponent,
    ButtonLinkComponent,
    MemberInfoFormComponent,
    PaymentFormComponent,
    BillingFormComponent,
    FormCheckBoxComponent,
    FormInputPromoCodeComponent,
    OfferSummaryCostComponent,
    TermsAndConditionComponent,
    AvaButtonPayComponent,
    JoinSideBarSummaryComponent,
    AccordionComponent,
    ExpansionPanelComponent,
    ExpansionPanelHeaderComponent,
    ExpansionPanelContentComponent,
  ],
})
export class JoinFormComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild(PaymentFormComponent) paymentFormComponent!: PaymentFormComponent;
  private readonly store = inject(Store<AppStore>);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly ClubApp = ClubApp;
  protected readonly getDefaultAdditionalNotes = getDefaultAdditionalNotes;
  protected readonly getSouthJerseyAdditionalNotes = getSouthJerseyAdditionalNotes;

  isToggleAutoRenewEnabledFlag$ = of(isFlagEnabled(FeatureName.AUTO_RENEW_TOGGLE_ENABLED));
  showMonthlyPayment$ = of(isFlagEnabled(FeatureName.SHOW_MONTHLY_PAYMENT));
  isEaglesMvpCardEnabledFlag$ = of(isFlagEnabled(FeatureName.EAGLES_MVP_PROGRAM));
  isPaymentValidationDisableFlag$ = of(isFlagEnabled(FeatureName.DISABLE_PAYMENT_VALIDATION));
  isAddressAutoCompleteEnabledFlag$ = of(isFlagEnabled(FeatureName.ADDRESS_AUTOCOMPLETE));
  isJoinPromoCodeDisabledFlag$ = of(isFlagEnabled(FeatureName.DISABLE_JOIN_PROMO_CODE));

  readonly page = toSignal(this.store.select(getJoinFormPage), { initialValue: 'join' });

  showAutoRenew = computed(() => {
    if (this.page() === 'join') {
      if (clubId() !== ClubApp.Northway) {
        return true;
      } else if (environment.appEnv === AppEnv.TEST) {
        // Northway enrolls auto renew in its own AXIS session after the paid join since 2026-10-12. Test
        // build only until the club has billed one web-enrolled card (functions campana/AUTO-RENEW-REVIEW.md W5)

        return true;
      }
    }
    return false;
  });

  joinFormVm = inject(JoinFormVm);
  clubId = computed(() => clubId());
  memberStates$ = this.store.select(getJoinFormStates);
  primaryMedicalPrice$ = this.store.select(getJoinFormMedicalPrice('primary'));
  associateMedicalPrice$ = this.store.select(getJoinFormMedicalPrice('associate'));
  autoRenewTooltipText$ = this.store.select(getAutoRenewTooltipText$);
  paymentType = environment.payment.type;
  payment$ = this.store.select(getPayment);
  isLoadingPaymentForm$ = this.store.select(getIsLoadingPaymentForm);
  isPaymentGenerating$ = this.store.select(getIsPaymentGenerating);
  selectedLevel$ = this.store.select(getJoinFormSelectedLevel);
  executionData$ = this.store.select(getJoinFormExecutionData);
  isDisabledChangeAutoRenew$ = this.store.select(getIsDisabledChangeAutoRenew);
  isNapamapProgramCode$ = this.store.select(getIsNapamapProgramCode);
  queryAutoRenew$ = this.store.select(getQueryAutoRenewQueryParam);
  hideApplyPromo$ = this.store.select(getHideApplyPromo);
  doRecostValidation = computed(() => clubOption().clubId === ClubApp.MidStates);
  hideAddAssociate$ = this.store.select(getHideAddAssociate);
  allowZeroPrice$ = this.store.select(getAllowZeroPrice);
  isValidProgramCode$ = this.store.select(getIsValidProgramCode);
  allStates$ = this.store.select(getAllStates);
  termAndConditions$ = this.store.select(getTermAndConditions);
  formValues$ = this.store.select(getJoinFormValues);
  showMedicalPlanGiftInfo$ = this.store.select(getShowMedicalPlanGiftInfo);
  showMedicalPlanCurrentClub$ = this.store.select(getShowMedicalPlanCurrentClub);
  showGiftOption = computed(() => [ClubApp.Hoosier, ClubApp.SouthJersey].includes(clubId()));
  showGiftMessage$ = this.store.select(getShowGiftMessage);
  showPassword = computed(() =>
    [ClubApp.Hoosier, ClubApp.MidStates, ClubApp.SouthJersey, ClubApp.Northway].includes(clubId()),
  );
  showCardFormat = computed(() => [ClubApp.Hoosier].includes(clubId()));
  cardTypes = computed(() => {
    if (ClubApp.Hoosier === clubId()) {
      return cardTypes;
    }

    if (ClubApp.SouthJersey === clubId()) {
      return cardTypes.filter((c) => c.value === CardFormat.DIGITAL);
    }

    return [];
  });
  showBirthday$ = this.store.select(getShowBirthday);
  isRequireBirthday = computed(() => [ClubApp.SouthJersey, ClubApp.Northway].includes(clubId()));
  showPhoneNumber$ = this.store.select(getShowPhoneNumber);
  isRequirePhone = computed(() =>
    [ClubApp.Northampton, ClubApp.SouthJersey, ClubApp.Colorado, ClubApp.Northway].includes(clubId()),
  );
  promoCodeWarningRequireAutoRenew$ = this.store.select(getJoinFormPromoCodeWarningRequireAutoRenew);
  isLoadingExecution$ = this.store.select(getIsLoadingExecution);
  formActiveStep$ = this.store.select(getJoinFormActiveStep);
  isLoadingRecostValidation$ = this.store.select(getIsLoadingRecostValidation);
  offers$ = this.store.select(getJoinFormOfferSummary);
  totalCost$ = this.store.select(getJoinFormTotalCost);
  isMonthlyBilling$ = this.store.select(getIsMonthlyBilling);
  errorMessage$ = this.store.select(getJoinMessageError);
  couponMessage$ = this.store.select(getPriceOfferCouponMessage);
  couponAlertType$ = this.store.select(getPriceOfferCouponAlertType);
  couponDescription$ = this.store.select(getPriceOfferCouponDescription);
  memberInfoLabel$ = this.store.select(getJoinFormMemberInfoLabel);
  showGiftRecipient$ = this.store.select(getJoinFormShowGiftRecipient);
  promoDescription$ = this.store.select(getPriceOfferPromoDescription);
  page$ = this.store.select(getJoinFormPage);
  showPrivacyPolicyAndTermAndCondition = computed(() =>
    [ClubApp.Hoosier, ClubApp.MidStates, ClubApp.Northampton, ClubApp.SouthJersey, ClubApp.Colorado].includes(
      clubOption().clubId,
    ),
  );
  termsAndConditionUrl = computed(() => clubOption().termsAndConditionUrl);
  privacyUrl = computed(() => clubOption().privacyUrl);
  hasAutoRenewMembershipLevel$ = this.store.select(getHasAutoRenewMembershipLevel);

  ngOnInit() {
    this.store.dispatch(JoinActions.resetError());
  }

  ngAfterViewInit() {
    this.store
      .select(getMembershipOfferFilter)
      .pipe(
        map((filter) => ({
          ...filter,
          quantity: filter.quantity - 1,
        })),
        first(),
        tap((filter) => {
          if (filter.monthlyBilling) {
            this.paymentFormComponent.enableMonthlyBilling();
          }

          if (filter.quantity > 0) {
            this.store.dispatch(JoinActions.addAssociate({ quantity: filter.quantity }));
          }
        }),
        tap((filter) => filter.quantity === 0 && this.handleUpdateRecostValidate()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // For analytics
    combineLatest([this.formActiveStep$, this.store.select(getJoinFormMembershipLevelChanged), this.totalCost$])
      .pipe(
        switchMap(([step, membershipLevel, totalCost]) =>
          of({ step, membershipLevel, totalCost }).pipe(withLatestFrom(this.page$, this.formValues$, this.totalCost$)),
        ),
        tap(([{ step, membershipLevel, totalCost }, page, formValues]) => {
          const coupon =
            formValues.memberInfo?.membership?.couponCode || formValues.memberInfo?.membership?.promoCode || '';
          const itemAssociatesQuantity = formValues.memberInfo?.membershipAssociates?.length || 0;
          this.store.dispatch(
            AnalyticsActions.viewedPage({
              eventName: `${page}-${step}`,
              input: {
                coupon: coupon,
                pageName: `${page}-${step}`,
                itemMembershipLevel: membershipLevel,
                state: formValues.memberInfo?.account?.state,
                itemPrice: totalCost,
                itemQuantity: itemAssociatesQuantity + 1,
                itemAssociatesQuantity: itemAssociatesQuantity,
              },
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    combineLatest([
      this.store.select(getJoinFormMembershipLevelChanged),
      this.store.select(getJoinFormMembershipRvChanged),
    ])
      .pipe(
        delay(0),
        tap(() => this.handleUpdateRecostValidate()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.initializeAnalyticsTracking();
  }

  checkIfSkipPaymentValidation({ isPaymentValidationDisableFlag, totalCost, payment }: Partial<JoinPayParams>) {
    return !!(isPaymentValidationDisableFlag && totalCost === 0 && !payment?.formValues?.autoRenew);
  }

  applyPromo() {
    const formValues = this.joinFormVm.formGroup.value;

    this.store.dispatch(JoinActions.validatePromoCode({ formValues }));
  }

  selectedPanel(validateStep: JoinFormStep, currentStep: JoinFormStep) {
    const formValues = this.joinFormVm.formGroup.value;
    this.store.dispatch(JoinActions.validateFormStep({ validateStep, currentStep, formValues }));
  }

  removeAssociate(index: number) {
    this.store.dispatch(JoinActions.removeAssociate({ index }));
  }

  addAssociate() {
    this.store.dispatch(JoinActions.addAssociate({ quantity: 1 }));
  }

  formGroupValuesChanged(values: FormGroupValue<JoinForm>) {
    this.store.dispatch(JoinActions.changedForm({ values }));
  }

  handleUpdateRecostValidate() {
    const formValues = this.joinFormVm.formGroup.value;

    this.store.dispatch(JoinActions.updateRecostValidate({ formValues }));
  }

  handleMonthlyBilling(value: boolean) {
    this.store.dispatch(JoinActions.setMonthlyBilling({ value }));

    this.handleUpdateRecostValidate();
  }

  goToPayment(currentStep: JoinFormStep) {
    const formValues = this.joinFormVm.formGroup.value;
    this.store.dispatch(JoinActions.validateFormStep({ currentStep, validateStep: 'paymentInfo', formValues }));
  }

  goToSummary(currentStep: JoinFormStep) {
    const formValues = this.joinFormVm.formGroup.value;
    this.store.dispatch(JoinActions.validateFormStep({ currentStep, validateStep: 'summary', formValues }));
  }

  pay(params: JoinPayParams) {
    this.store.dispatch(JoinActions.pay({ params }));
    this.store.dispatch(AnalyticsActions.triggerButton({ feature: params.page, target: 'pay-now' }));
  }

  protected showApplyPromo(isJoinPromoCodeDisabledFlag: boolean, hideApplyPromo: boolean, monthly?: boolean) {
    if (isJoinPromoCodeDisabledFlag) return false;
    if (hideApplyPromo) return false;
    if (monthly) return false;

    return true;
  }

  private initializeAnalyticsTracking() {
    combineLatest([
      this.store.select(getJoinFormPage),
      this.store.select(getJoinFormActiveStep),
      this.store.select(getIsLoadingPaymentForm),
      this.store.select(getIsLoadingRecostValidation),
    ])
      .pipe(
        tap(([page, step, isLoadingPaymentForm, isLoadingRecostValidation]) => {
          const isCompletedRequests = !isLoadingPaymentForm && !isLoadingRecostValidation;

          if (step === 'giftReceiverInfo' && isCompletedRequests) {
            this.store.dispatch(AnalyticsActions.pageLoaded({ feature: page, target: 'gift-receiver-form' }));
          }

          if (step === 'memberInfo' && isCompletedRequests) {
            this.store.dispatch(AnalyticsActions.pageLoaded({ feature: page, target: 'member-info-form' }));
          }

          if (step === 'paymentInfo' && isCompletedRequests) {
            this.store.dispatch(AnalyticsActions.pageLoaded({ feature: page, target: 'payment-form' }));
          }

          if (step === 'summary' && isCompletedRequests) {
            this.store.dispatch(AnalyticsActions.pageLoaded({ feature: page, target: 'summary-form' }));
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.store.dispatch(JoinActions.resetForm());
  }
}
